import React, { useMemo, useState } from 'react';
import { ArrowLeftIcon, CameraIcon, CheckCircle2Icon, ClockIcon, FileIcon, ImageIcon, PackageIcon, PlayIcon, PlusIcon, ReceiptIcon, Trash2Icon } from 'lucide-react';
import { Employee, JobStatus, Material, ProJob, TimeEntry, WorkType } from '../../types/pro';
import { JOB_STATUS, formatDate, formatTime, hoursOnJob, kr, newLineId, toISODate } from '../../utils/pro';
import { StatusChip } from '../../components/pro/StatusChip';
import { EmptyState } from '../../components/pro/EmptyState';
import { TimeEntryList } from '../../components/pro/TimeEntryList';
interface ProJobDetailProps {
  job: ProJob;
  employees: Employee[];
  materials: Material[];
  timeEntries: TimeEntry[];
  companyHourlyRate: number;
  onBack: () => void;
  onUpdate: (changes: Partial<ProJob>) => void;
  onAddTime: (entry: Omit<TimeEntry, 'id'>) => void;
  onUpdateTime: (id: string, changes: Partial<TimeEntry>) => void;
  onRemoveTime: (id: string) => void;
  onCreateInvoice: (job: ProJob) => void;
}
type Tab = 'overblik' | 'timer' | 'materialer' | 'billeder' | 'dokumenter' | 'noter';
const TABS: {
  id: Tab;
  label: string;
}[] = [{
  id: 'overblik',
  label: 'Overblik'
}, {
  id: 'timer',
  label: 'Timer'
}, {
  id: 'materialer',
  label: 'Materialer'
}, {
  id: 'billeder',
  label: 'Billeder'
}, {
  id: 'dokumenter',
  label: 'Dokumenter'
}, {
  id: 'noter',
  label: 'Noter'
}];
const WORK_TYPES: WorkType[] = ['Montage', 'Fejlfinding', 'Service', 'Kørsel', 'Andet'];
export function ProJobDetail({
  job,
  employees,
  materials,
  timeEntries,
  companyHourlyRate,
  onBack,
  onUpdate,
  onAddTime,
  onUpdateTime,
  onRemoveTime,
  onCreateInvoice
}: ProJobDetailProps) {
  const [tab, setTab] = useState<Tab>('overblik');
  const [timeForm, setTimeForm] = useState({
    employeeId: employees[0]?.id ?? '',
    hours: '',
    workType: 'Montage' as WorkType,
    note: ''
  });
  const [materialPick, setMaterialPick] = useState('');
  const [materialQty, setMaterialQty] = useState('1');
  const own = useMemo(() => timeEntries.filter(entry => entry.jobId === job.id), [timeEntries, job.id]);
  const registered = hoursOnJob(timeEntries, job.id ?? '');
  const overrun = job.estimatedHours > 0 && registered > job.estimatedHours;
  const materialTotal = job.materials.reduce((sum, item) => sum + item.quantity * item.salesPrice, 0);
  const usesOwnRate = typeof job.hourlyRate === 'number';
  const rate = usesOwnRate ? job.hourlyRate as number : companyHourlyRate;
  const labourTotal = registered * rate;
  const setStatus = (status: JobStatus) => onUpdate({
    status
  });
  const addTime = () => {
    const hours = parseFloat(timeForm.hours.replace(',', '.'));
    if (!hours || !timeForm.employeeId || !job.id) return;
    onAddTime({
      employeeId: timeForm.employeeId,
      jobId: job.id,
      workType: timeForm.workType,
      date: toISODate(new Date()),
      hours,
      note: timeForm.note.trim(),
      billable: timeForm.workType !== 'Kørsel'
    });
    setTimeForm({
      ...timeForm,
      hours: '',
      note: ''
    });
  };
  const addMaterial = () => {
    const material = materials.find(m => m.id === materialPick);
    const quantity = parseFloat(materialQty.replace(',', '.'));
    if (!material || !quantity) return;
    onUpdate({
      materials: [...job.materials, {
        id: newLineId(),
        materialId: material.id,
        name: material.name,
        itemNo: material.itemNo,
        quantity,
        costPrice: material.costPrice,
        salesPrice: material.salesPrice
      }]
    });
    setMaterialPick('');
    setMaterialQty('1');
  };
  const card = 'rounded-xl border border-stone-200 bg-white';
  const field = 'w-full rounded-lg border border-stone-200 px-3 py-2.5 text-sm outline-none focus:border-transparent focus:ring-2 focus:ring-brand-500';
  const actions = <>
      {job.status !== 'i-gang' && job.status !== 'faerdig' && <button onClick={() => setStatus('i-gang')} className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-stone-900 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-stone-800" data-id="element-975" data-mp="1adtl54y1jxrd">
          <PlayIcon className="h-4 w-4" data-id="element-976" data-mp="1o45f79e6s6l5" />
          Start arbejde
        </button>}
      <button onClick={() => setTab('timer')} className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg border border-stone-200 bg-white px-4 py-3 text-sm font-semibold text-stone-700 transition-colors hover:bg-stone-50" data-id="element-977" data-mp="1o601os53w6rc">
        <ClockIcon className="h-4 w-4" data-id="element-978" data-mp="232r294qinoij" />
        Registrer tid
      </button>
      <button onClick={() => setTab('materialer')} className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg border border-stone-200 bg-white px-4 py-3 text-sm font-semibold text-stone-700 transition-colors hover:bg-stone-50" data-id="element-979" data-mp="273aze8b3as4o">
        <PackageIcon className="h-4 w-4" data-id="element-980" data-mp="55h98dhdvi1i" />
        Tilføj materiale
      </button>
      {job.status !== 'faerdig' && job.status !== 'faktureret' ? <button onClick={() => setStatus('faerdig')} className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-emerald-700" data-id="element-981" data-mp="dz8nqj9vjh2">
          <CheckCircle2Icon className="h-4 w-4" data-id="element-982" data-mp="o43bvwsjth7j" />
          Afslut opgave
        </button> : job.status === 'faerdig' && <button onClick={() => onCreateInvoice(job)} className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-brand-600 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-brand-700" data-id="element-983" data-mp="j6jwuuo110ej">
            <ReceiptIcon className="h-4 w-4" data-id="element-984" data-mp="2ztmhx18i0r7e" />
            Opret faktura
          </button>}
    </>;
  return <div className="pb-24 lg:pb-0" data-id="element-985" data-mp="2utrninpiljhl">
      <button onClick={onBack} className="mb-4 inline-flex items-center gap-2 text-sm font-medium text-stone-500 transition-colors hover:text-stone-900" data-id="element-986" data-mp="2uww0a9h1pnjv">
        <ArrowLeftIcon className="h-4 w-4" data-id="element-987" data-mp="31ajg6d90ey9l" />
        Alle opgaver
      </button>

      <header className="mb-5 flex flex-wrap items-start justify-between gap-4" data-id="element-988" data-mp="2na8ox93sfhnw">
        <div className="min-w-0" data-id="element-989" data-mp="2rccaiqk682mw">
          <div className="flex flex-wrap items-center gap-2.5" data-id="element-990" data-mp="2rbanrotslrpc">
            <p className="font-mono text-xs font-semibold text-brand-700" data-id="element-991" data-mp="2dkyyrysi892f">
              {job.jobNo}
            </p>
            <StatusChip meta={JOB_STATUS[job.status]} data-id="element-992" data-mp="2id8qnkd6jj7c" />
          </div>
          <h1 className="mt-1 text-2xl font-bold tracking-tight text-stone-900 sm:text-3xl" data-id="element-993" data-mp="23nknsepcochi">
            {job.title}
          </h1>
          <p className="mt-1 text-sm text-stone-500" data-id="element-994" data-mp="28n5puxzth0yg">
            {job.customerName} · {job.address}, {job.city}
          </p>
        </div>
        <label className="flex-shrink-0" data-id="element-995" data-mp="1uoz0k68xo9ae">
          <span className="sr-only" data-id="element-996" data-mp="1zlz7skhiri33">Skift status</span>
          <select value={job.status} onChange={e => setStatus(e.target.value as JobStatus)} className="rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm font-medium outline-none focus:ring-2 focus:ring-brand-500" data-id="element-997" data-mp="1znioudb01x5x">
            {(Object.keys(JOB_STATUS) as JobStatus[]).map(status => <option key={status} value={status} data-id="element-998" data-mp="1kxxq8jt5yp9i">
                {JOB_STATUS[status].label}
              </option>)}
          </select>
        </label>
      </header>

      {/* Estimated vs registered, side by side while the job is still running */}
      <section className={`${card} mb-5 grid grid-cols-2 gap-px overflow-hidden bg-stone-200 sm:grid-cols-4`} data-id="element-999" data-mp="28olbe6s3bbe0">
        {[{
        label: 'Estimeret',
        value: `${job.estimatedHours} t`
      }, {
        label: 'Registreret',
        value: `${registered.toLocaleString('da-DK')} t`,
        alert: overrun
      }, {
        label: 'Materialer',
        value: kr(materialTotal)
      }, {
        label: 'Arbejdsløn',
        value: kr(labourTotal),
        hint: `${rate.toLocaleString('da-DK')} kr/t${usesOwnRate ? ' · egen pris' : ''}`
      }].map(stat => <div key={stat.label} className="bg-white p-4" data-id="element-1000" data-mp="3t3tyjok3bdp0">
            <p className={`text-[11px] font-semibold uppercase tracking-wider ${stat.alert ? 'text-amber-600' : 'text-stone-400'}`} data-id="element-1001" data-mp="1xixvtizsey7">
              {stat.label}
            </p>
            <p className={`mt-1 text-lg font-bold tabular-nums ${stat.alert ? 'text-amber-600' : 'text-stone-900'}`} data-id="element-1002" data-mp="35e99qp199m7t">
              {stat.value}
            </p>
            {stat.hint && <p className={`mt-0.5 text-xs ${usesOwnRate ? 'font-medium text-brand-700' : 'text-stone-400'}`} data-id="element-1003" data-mp="2hoexo9org1om">
                {stat.hint}
              </p>}
          </div>)}
      </section>

      <div role="tablist" aria-label="Opgavesektioner" className="mb-5 flex gap-1 overflow-x-auto rounded-xl border border-stone-200 bg-white p-1" data-id="element-1004" data-mp="2f1cwyh01bsl7">
        {TABS.map(item => <button key={item.id} role="tab" aria-selected={tab === item.id} onClick={() => setTab(item.id)} className={`whitespace-nowrap rounded-lg px-3.5 py-2 text-sm font-medium transition-colors ${tab === item.id ? 'bg-brand-50 text-brand-700' : 'text-stone-500 hover:text-stone-900'}`} data-id="element-1005" data-mp="213y1auj6uoks">
            {item.label}
          </button>)}
      </div>

      {tab === 'overblik' && <div className="space-y-5" data-id="element-1006" data-mp="1demzd9q3qo52">
          <section className={`${card} p-5`} data-id="element-1007" data-mp="1ddty2o9wwqub">
            <h2 className="mb-3 text-base font-semibold text-stone-900" data-id="element-1008" data-mp="18fitot2dz8jh">
              Beskrivelse
            </h2>
            <textarea value={job.description} onChange={e => onUpdate({
          description: e.target.value
        })} rows={4} className={field} data-id="element-1009" data-mp="2v797p1ngco1x" />
          </section>

          <section className={`${card} p-5`} data-id="element-1010" data-mp="34wj1clxy2es5">
            <h2 className="mb-4 text-base font-semibold text-stone-900" data-id="element-1011" data-mp="2zyr6pk5x0ek9">
              Planlægning
            </h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3" data-id="element-1012" data-mp="2c8n3vf0jwiub">
              <label className="block" data-id="element-1013" data-mp="2cazvkjw427kk">
                <span className="mb-1.5 block text-sm font-medium text-stone-700" data-id="element-1014" data-mp="27avfguz47euv">
                  Start
                </span>
                <input type="datetime-local" value={job.start ? job.start.slice(0, 16) : ''} onChange={e => onUpdate({
              start: e.target.value ? new Date(e.target.value).toISOString() : undefined,
              status: job.status === 'ikke-planlagt' && e.target.value ? 'planlagt' : job.status
            })} className={field} data-id="element-1015" data-mp="2m8xhh0qhzsa0" />
              </label>
              <label className="block" data-id="element-1016" data-mp="3v7uva1m7qdwq">
                <span className="mb-1.5 block text-sm font-medium text-stone-700" data-id="element-1017" data-mp="3v8w7waivn8gk">
                  Varighed (timer)
                </span>
                <input type="number" min={1} value={job.durationHours} onChange={e => onUpdate({
              durationHours: parseFloat(e.target.value) || 1
            })} className={field} data-id="element-1018" data-mp="mudr9keveklc" />
              </label>
              <label className="block" data-id="element-1019" data-mp="15qdpve2csmdw">
                <span className="mb-1.5 block text-sm font-medium text-stone-700" data-id="element-1020" data-mp="15pm2gqiwtuyp">
                  Estimerede timer
                </span>
                <input type="number" min={0} value={job.estimatedHours} onChange={e => onUpdate({
              estimatedHours: parseFloat(e.target.value) || 0
            })} className={field} data-id="element-1021" data-mp="1kdccmvq9msma" />
              </label>
            </div>

            {/* A job can be priced away from the company rate — a service
                agreement, a discount, or an evening call-out. */}
            <div className="mt-4 border-t border-stone-100 pt-4" data-id="element-1022" data-mp="wo9wjsba57tw">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3" data-id="element-1023" data-mp="wmqcdqmi6cmu">
                <label className="block" data-id="element-1024" data-mp="sn7vr71psnpt">
                  <span className="mb-1.5 block text-sm font-medium text-stone-700" data-id="element-1025" data-mp="sk24bl5m59qx">
                    Timepris (kr)
                  </span>
                  <input type="number" min={0} step={5} value={rate} onChange={e => onUpdate({
                hourlyRate: Math.max(0, parseFloat(e.target.value) || 0)
              })} className={field} data-id="element-1026" data-mp="in7858wtblft" />
                </label>
                <div className="sm:col-span-2 sm:pt-7" data-id="element-1027" data-mp="dk6fad52mkx1">
                  <p className="text-sm text-stone-500" data-id="element-1028" data-mp="ij153z1gh9qn">
                    {usesOwnRate ? <>
                        Denne opgave bruger sin egen pris. Firmaets standard er{' '}
                        {companyHourlyRate.toLocaleString('da-DK')} kr/t.{' '}
                        <button onClick={() => onUpdate({
                    hourlyRate: null
                  })} className="font-medium text-brand-700 underline underline-offset-2 transition-colors hover:text-brand-800" data-id="element-1029" data-mp="3qykwesikgi32">
                          Brug standardprisen
                        </button>
                      </> : <>
                        Følger firmaets standardpris. Ændrer du tallet, gælder
                        det kun denne opgave — og slår igennem på fakturaen.
                      </>}
                  </p>
                </div>
              </div>
            </div>

            <fieldset className="mt-4" data-id="element-1030" data-mp="1druz0flrkdr3">
              <legend className="mb-2 text-sm font-medium text-stone-700" data-id="element-1031" data-mp="1dq0fn5q54tp4">
                Tildelte medarbejdere
              </legend>
              <div className="flex flex-wrap gap-2" data-id="element-1032" data-mp="1rkuhmqgueo2v">
                {employees.map(employee => {
              const isOn = job.employeeIds.includes(employee.id ?? '');
              return <button key={employee.id} onClick={() => onUpdate({
                employeeIds: isOn ? job.employeeIds.filter(id => id !== employee.id) : [...job.employeeIds, employee.id ?? '']
              })} aria-pressed={isOn} className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${isOn ? 'border-brand-300 bg-brand-50 text-brand-700' : 'border-stone-200 text-stone-600 hover:bg-stone-50'}`} data-id="element-1033" data-mp="1wi2zepy2wq3l">
                      <span className="h-2 w-2 rounded-full" style={{
                  backgroundColor: employee.color
                }} data-id="element-1034" data-mp="201lbdwmjzaf5" />
                      {employee.name}
                    </button>;
            })}
              </div>
            </fieldset>
          </section>
        </div>}

      {tab === 'timer' && <div className="space-y-5" data-id="element-1035" data-mp="3kekdikfqwiq5">
          <section className={`${card} p-5`} data-id="element-1036" data-mp="3kdhjw4r1g26j">
            <h2 className="mb-4 text-base font-semibold text-stone-900" data-id="element-1037" data-mp="3izpxh6a5b0i4">
              Registrer tid
            </h2>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-4" data-id="element-1038" data-mp="3dy0m1pj0gkti">
              <select value={timeForm.employeeId} onChange={e => setTimeForm({
            ...timeForm,
            employeeId: e.target.value
          })} aria-label="Medarbejder" className={field} data-id="element-1039" data-mp="38zqq3cpgpg6u">
                {employees.map(employee => <option key={employee.id} value={employee.id} data-id="element-1040" data-mp="l8nhqiaisum">
                    {employee.name}
                  </option>)}
              </select>
              <select value={timeForm.workType} onChange={e => setTimeForm({
            ...timeForm,
            workType: e.target.value as WorkType
          })} aria-label="Arbejdstype" className={field} data-id="element-1041" data-mp="3nykpmyu061ew">
                {WORK_TYPES.map(type => <option key={type} value={type} data-id="element-1042" data-mp="274mc7g5lv9tl">
                    {type}
                  </option>)}
              </select>
              <input value={timeForm.hours} onChange={e => setTimeForm({
            ...timeForm,
            hours: e.target.value
          })} placeholder="Timer, f.eks. 2,5" inputMode="decimal" aria-label="Timer" className={field} data-id="element-1043" data-mp="2b5ofc82n860u" />
              <button onClick={addTime} className="rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-700" data-id="element-1044" data-mp="2uuqe9j35b1ih">
                Tilføj
              </button>
            </div>
            <input value={timeForm.note} onChange={e => setTimeForm({
          ...timeForm,
          note: e.target.value
        })} placeholder="Note (valgfri)" className={`${field} mt-3`} data-id="element-1045" data-mp="38ureqxfi06xf" />
          </section>

          <section className={`${card} overflow-hidden`} data-id="element-1046" data-mp="2eum061fq7ol3">
            <header className="flex items-center justify-between gap-3 border-b border-stone-100 px-5 py-3" data-id="element-1047" data-mp="2sr92kjqg76ye">
              <h2 className="text-base font-semibold text-stone-900" data-id="element-1048" data-mp="2or72gf0d2ll8">
                Registreret på opgaven
              </h2>
              <span className="text-sm font-semibold tabular-nums text-stone-900" data-id="element-1049" data-mp="20z081le74oog">
                {registered.toLocaleString('da-DK')} t
              </span>
            </header>
            <TimeEntryList entries={own} emptyTitle="Ingen timer registreret" onUpdate={onUpdateTime} onRemove={onRemoveTime} data-id="element-1050" data-mp="29uzm5ps2q4b8" />
          </section>
        </div>}

      {tab === 'materialer' && <div className="space-y-5" data-id="element-1051" data-mp="1dah3ngy9ow1n">
          <section className={`${card} p-5`} data-id="element-1052" data-mp="1dba4606mupbc">
            <h2 className="mb-4 text-base font-semibold text-stone-900" data-id="element-1053" data-mp="137200vdji7h5">
              Tilføj fra kartotek
            </h2>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_120px_auto]" data-id="element-1054" data-mp="17z3ln7wllaju">
              <select value={materialPick} onChange={e => setMaterialPick(e.target.value)} aria-label="Materiale" className={field} data-id="element-1055" data-mp="ta750mgh6p7m">
                <option value="" data-id="element-1056" data-mp="1lzedad5qet62">Vælg materiale…</option>
                {materials.map(material => <option key={material.id} value={material.id} data-id="element-1057" data-mp="1qwnyfgm38io3">
                    {material.name} — {kr(material.salesPrice)}/{material.unit}
                  </option>)}
              </select>
              <input value={materialQty} onChange={e => setMaterialQty(e.target.value)} inputMode="decimal" aria-label="Antal" className={field} data-id="element-1058" data-mp="1gx5hl91h5d09" />
              <button onClick={addMaterial} className="inline-flex items-center justify-center gap-2 rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-700" data-id="element-1059" data-mp="31kr2xpitbmgi">
                <PlusIcon className="h-4 w-4" data-id="element-1060" data-mp="2sjjgq5tkmc2s" />
                Tilføj
              </button>
            </div>
          </section>

          <section className={`${card} overflow-hidden`} data-id="element-1061" data-mp="2itzwowu4gc6c">
            {job.materials.length === 0 ? <EmptyState icon={PackageIcon} title="Ingen materialer endnu" data-id="element-1062" data-mp="2nkg9qfu15vs6" /> : <>
                <ul className="divide-y divide-stone-100" data-id="element-1063" data-mp="29tux38v66i2q">
                  {job.materials.map(item => <li key={item.id} className="flex items-center gap-4 px-5 py-3" data-id="element-1064" data-mp="2dvydmbwp4cuv">
                      <span className="min-w-0 flex-1" data-id="element-1065" data-mp="1zxhwdr17j3yh">
                        <span className="block truncate text-sm font-medium text-stone-900" data-id="element-1066" data-mp="24sx74a3x4jqo">
                          {item.name}
                        </span>
                        <span className="block font-mono text-xs text-stone-400" data-id="element-1067" data-mp="1g9lopp0whpkw">
                          {item.itemNo}
                        </span>
                      </span>
                      <span className="text-sm tabular-nums text-stone-600" data-id="element-1068" data-mp="1v49tt22e2lzz">
                        {item.quantity} × {kr(item.salesPrice)}
                      </span>
                      <span className="w-24 text-right text-sm font-semibold tabular-nums text-stone-900" data-id="element-1069" data-mp="245q479t7eo47">
                        {kr(item.quantity * item.salesPrice)}
                      </span>
                      <button onClick={() => onUpdate({
                materials: job.materials.filter(m => m.id !== item.id)
              })} aria-label={`Fjern ${item.name}`} className="rounded-lg p-1.5 text-stone-300 transition-colors hover:bg-red-50 hover:text-red-600" data-id="element-1070" data-mp="2dwkrmhi6rtr8">
                        <Trash2Icon className="h-4 w-4" data-id="element-1071" data-mp="2uyrp61oj2984" />
                      </button>
                    </li>)}
                </ul>
                <div className="flex items-center justify-between border-t border-stone-200 bg-stone-50 px-5 py-3" data-id="element-1072" data-mp="34ojjqvwmn2cl">
                  <span className="text-sm font-medium text-stone-600" data-id="element-1073" data-mp="38ov5kc0q78pn">
                    Materialer i alt
                  </span>
                  <span className="text-sm font-bold tabular-nums text-stone-900" data-id="element-1074" data-mp="3dr2j3617ewu3">
                    {kr(materialTotal)}
                  </span>
                </div>
              </>}
          </section>
        </div>}

      {tab === 'billeder' && <section className={`${card} p-5`} data-id="element-1075" data-mp="1obcugwzv2mcr">
          <button onClick={() => onUpdate({
        photos: [...job.photos, {
          id: newLineId(),
          url: '',
          caption: 'Nyt billede',
          uploadedAt: new Date().toISOString()
        }]
      })} className="flex w-full flex-col items-center gap-2 rounded-xl border border-dashed border-stone-300 px-6 py-10 text-sm font-medium text-stone-500 transition-colors hover:border-brand-300 hover:text-brand-700" data-id="element-1076" data-mp="1t94obwk2gijl">
            <CameraIcon className="h-6 w-6" data-id="element-1077" data-mp="par94kua0clj" />
            Tilføj billede fra kameraet
          </button>

          {job.photos.length > 0 && <ul className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4" data-id="element-1078" data-mp="kacxkstgukir">
              {job.photos.map(photo => <li key={photo.id} className="overflow-hidden rounded-lg border border-stone-200" data-id="element-1079" data-mp="ffh4rqgent4l">
                  <div className="flex aspect-square items-center justify-center bg-stone-100" data-id="element-1080" data-mp="302aik69ysa4m">
                    <ImageIcon className="h-6 w-6 text-stone-300" data-id="element-1081" data-mp="rtkv3ls2o78k" />
                  </div>
                  <p className="truncate px-2 py-1.5 text-xs text-stone-600" data-id="element-1082" data-mp="wlm2atadv7rq">
                    {photo.caption}
                  </p>
                </li>)}
            </ul>}
        </section>}

      {tab === 'dokumenter' && <EmptyState icon={FileIcon} title="Ingen dokumenter" description="Tegninger og aftaler til denne opgave lægges her." data-id="element-1083" data-mp="nqo69dbxvew6" />}

      {tab === 'noter' && <section className={`${card} p-5`} data-id="element-1084" data-mp="1fhvne0jpcyl8">
          <label className="block" data-id="element-1085" data-mp="11jdw5sul93yk">
            <span className="mb-2 block text-sm font-medium text-stone-700" data-id="element-1086" data-mp="11mjl9837w5ua">
              Noter til opgaven
            </span>
            <textarea value={job.notes} onChange={e => onUpdate({
          notes: e.target.value
        })} rows={8} placeholder="Adgangsforhold, aftaler med kunden, hvad der mangler…" className={`${field} leading-relaxed`} data-id="element-1087" data-mp="3maxudfqx09t8" />
          </label>
        </section>}

      {/* Desktop actions */}
      <div className="mt-6 hidden gap-3 lg:flex" data-id="element-1088" data-mp="3eqvjp127ydtf">{actions}</div>

      {/* Phone action bar — thumb-sized, always reachable */}
      <div className="fixed inset-x-0 bottom-[52px] z-30 flex gap-2 border-t border-stone-200 bg-white/95 px-3 py-2.5 backdrop-blur lg:hidden" data-id="element-1089" data-mp="3nrtr7tbv93gr">
        {actions}
      </div>
    </div>;
}